/**
 * KYC Financial Info Mock Data Generator
 *
 * 该模块提供 KYC 财务信息表单的模拟数据生成功能
 * 使用 @faker-js/faker 生成符合表单验证规则的测试数据
 *
 * @module mockDataGenerator.financial
 * @description 财务信息模拟数据生成器
 */

import {
  EmploymentStatusOptions,
  IncomeOptions,
  IncomeSourceOptions,
  IndustryOptions,
  TradingPurposeOptions,
} from "@/types/enum";
import { faker } from "@faker-js/faker";

/* ============================================================================
 * 类型定义
 * ========================================================================== */

/**
 * 国家数据类型
 */
interface CountryData {
  code2: string;
  cn: string;
  en: string;
  hk: string;
  tel: string;
  url: string;
  priority: number;
}

/**
 * 国家JSON数据结构
 */
interface CountryJsonData {
  code: string;
  msg: string;
  data: CountryData[];
}

// 导入真实的国家数据
import countryAllDataJson from "../../data/country.json";
import countryAllowedDataJson from "../../data/country-allowed.json";

const countryAllData = countryAllDataJson as CountryJsonData;
const countryAllowedData = countryAllowedDataJson as CountryJsonData;

/**
 * 财务信息表单数据类型
 */
export interface FinancialInfoFormData {
  /** 就业状态 */
  employmentStatus: string;
  /** 行业 */
  industry: string;
  /** 年收入 */
  annualIncome: string;
  /** 收入来源地区（ISO 3166-1 alpha-3 代码） */
  incomeSourceCountry: string;
  /** 收入来源（数组） */
  incomeSource: string[];
  /** 收入来源备注（当选择"其他"时，独立字段） */
  incomeSourceRemark?: string;
  /** 初始资金来源 */
  initialFundSource: {
    /** 是否与收入来源相同 */
    sameAsIncome: boolean;
    /** 资金来源列表（当不同时） */
    sources: string[];
  };
  /** 初始资金来源备注（当选择"其他"时，独立字段） */
  initialFundSourceRemark?: string;
  /** 交易目的（数组） */
  transactionPurpose: string[];
  /** 净资产（USD） */
  netWorth: string;
  /** 虚拟资产占比（%） */
  otherPlatformNetWorthRatio: string;
  /** 是否为美国公民 */
  isUSPerson: "YES" | "NO";
  /** 税务管辖区域列表（当isUSPerson为'NO'时必填） */
  tins?: TaxJurisdiction[];
}

/**
 * 税务管辖区域
 */
export interface TaxJurisdiction {
  /** 国家/地区代码 */
  tins_country: string;
  /** 税务识别号 */
  tins_taxId: string;
}

/**
 * Mock 数据生成配置选项
 */
export interface FinancialMockDataGeneratorOptions {
  /** 指定就业状态 */
  employmentStatus?: string;
  /** 指定行业 */
  industry?: string;
  /** 指定收入来源国家 */
  incomeSourceCountry?: string;
  /** 是否为美国公民（默认：否） */
  isUSPerson?: "YES" | "NO";
  /** 初始资金来源是否与收入来源相同 */
  initialFundSameAsIncome?: boolean;
  /** 自定义种子 */
  seed?: number;
}

/* ============================================================================
 * 常量配置
 * ========================================================================== */

/**
 * 就业状态选项（来自 strapi-enum.ts）
 */
const EMPLOYMENT_STATUS_OPTIONS = Object.values(EmploymentStatusOptions);

/**
 * 行业选项（来自 strapi-enum.ts）
 * 为了避免列表过长，选择一些常用的行业
 */
const COMMON_INDUSTRY_OPTIONS = [
  IndustryOptions.Information_and_Communication_Workers,
  IndustryOptions.Financial_and_Insurance_Workers,
  IndustryOptions.Professional_Scientific_and_Technical_Service_Workers,
  IndustryOptions.Education_Service_Workers,
  IndustryOptions.Health_and_Social_Work_Service_Workers,
  IndustryOptions.Manufacturing_Workers,
  IndustryOptions.Wholesale_and_Retail_Trade_Workers,
  IndustryOptions.Real_Estate_Workers,
  IndustryOptions.Self_employed,
  IndustryOptions.Retired,
  IndustryOptions.Unemployed,
];

/**
 * 年收入选项（来自 strapi-enum.ts）
 */
const ANNUAL_INCOME_OPTIONS = Object.values(IncomeOptions);

/**
 * 收入来源选项（来自 strapi-enum.ts）
 */
const INCOME_SOURCE_OPTIONS = Object.values(IncomeSourceOptions);

/**
 * 交易目的选项（来自 strapi-enum.ts）
 */
const TRANSACTION_PURPOSE_OPTIONS = Object.values(TradingPurposeOptions);

/**
 * 所有国家列表（来自 country.json）
 * 用于税务管辖区域
 */
const ALL_COUNTRIES = countryAllData.data;

/**
 * 允许的国家列表（来自 country-allowed.json）
 * 用于收入来源地区
 */
const ALLOWED_COUNTRIES = countryAllowedData.data;

/**
 * 所有国家代码列表（用于税务管辖区域）
 */
const ALL_COUNTRY_CODES = ALL_COUNTRIES.map((c: CountryData) => c.code2);

/**
 * 允许的国家代码列表（用于收入来源地区）
 */
const ALLOWED_COUNTRY_CODES = ALLOWED_COUNTRIES.map(
  (c: CountryData) => c.code2
);

/* ============================================================================
 * 辅助函数
 * ========================================================================== */

/**
 * 生成净资产金额（USD）
 *
 * @param fakerInstance - faker 实例
 * @returns 净资产金额字符串
 */
function generateNetWorth(fakerInstance: typeof faker): string {
  // 生成 10,000 到 10,000,000 之间的金额
  const amount = fakerInstance.number.int({ min: 10000, max: 10000000 });
  return String(amount);
}

/**
 * 生成虚拟资产占比（%）
 *
 * @param fakerInstance - faker 实例
 * @returns 占比百分比字符串（0-30，符合监管要求）
 */
function generateAssetRatio(fakerInstance: typeof faker): string {
  // 限制在 0-30% 之间（监管要求不超过30%）
  const ratio = fakerInstance.number.int({ min: 0, max: 30 });
  return String(ratio);
}

/**
 * 生成税务识别号
 *
 * @param fakerInstance - faker 实例
 * @param country - 国家代码
 * @returns 税务识别号
 */
function generateTIN(fakerInstance: typeof faker, country: string): string {
  // 不同国家有不同的 TIN 格式，这里简化处理
  if (country === "HKG") {
    // 香港：字母 + 6-7 位数字
    return (
      fakerInstance.string.alpha({ length: 1, casing: "upper" }) +
      fakerInstance.string.numeric(7)
    );
  } else if (country === "CHN") {
    // 中国：18 位统一社会信用代码
    return fakerInstance.string.numeric(18);
  } else {
    // 其他：字母数字混合
    return fakerInstance.string.alphanumeric({ length: 10, casing: "upper" });
  }
}

/**
 * 生成税务管辖区域列表
 *
 * @param fakerInstance - faker 实例
 * @returns 税务管辖区域列表
 */
function generateTaxJurisdictions(
  fakerInstance: typeof faker
): TaxJurisdiction[] {
  // 随机生成 1-2 个税务管辖区域
  const count = fakerInstance.number.int({ min: 1, max: 2 });
  const selectedCountries = fakerInstance.helpers.arrayElements(
    ALL_COUNTRY_CODES,
    count
  );

  return selectedCountries.map((country: string) => ({
    tins_country: country,
    tins_taxId: generateTIN(fakerInstance, country),
  }));
}

/* ============================================================================
 * 主函数
 * ========================================================================== */

/**
 * 生成财务信息表单模拟数据
 *
 * @param options - 生成配置选项
 * @returns 符合表单验证规则的模拟数据
 *
 * @example
 * ```typescript
 * // 生成基本财务信息
 * const basicData = generateFinancialInfoMockData();
 *
 * // 生成指定就业状态的数据
 * const employedData = generateFinancialInfoMockData({
 *   employmentStatus: 'EMPLOYED',
 *   industry: 'TECHNOLOGY'
 * });
 *
 * // 生成可重复的数据
 * const repeatableData = generateFinancialInfoMockData({
 *   seed: 12345
 * });
 * ```
 */
export function generateFinancialInfoMockData(
  options: FinancialMockDataGeneratorOptions = {}
): FinancialInfoFormData {
  const {
    employmentStatus: specifiedEmploymentStatus,
    industry: specifiedIndustry,
    incomeSourceCountry: specifiedCountry,
    isUSPerson: specifiedIsUSPerson = "NO",
    initialFundSameAsIncome = undefined,
    seed,
  } = options;

  // 创建 faker 实例
  const fakerInstance = seed !== undefined ? faker : faker;
  if (seed !== undefined) {
    fakerInstance.seed(seed);
  }

  // 确定就业状态
  const employmentStatus =
    specifiedEmploymentStatus ||
    fakerInstance.helpers.arrayElement(EMPLOYMENT_STATUS_OPTIONS);

  // 确定行业
  const industry =
    specifiedIndustry ||
    fakerInstance.helpers.arrayElement(COMMON_INDUSTRY_OPTIONS);

  // 确定年收入
  const annualIncome = fakerInstance.helpers.arrayElement(
    ANNUAL_INCOME_OPTIONS
  );

  // 确定收入来源国家（从允许的国家中随机选择）
  const incomeSourceCountry =
    specifiedCountry ||
    fakerInstance.helpers.arrayElement(ALLOWED_COUNTRY_CODES);

  // 确定收入来源（1-3 个）
  const incomeSourceCount = fakerInstance.number.int({ min: 1, max: 3 });
  const incomeSource = fakerInstance.helpers.arrayElements(
    INCOME_SOURCE_OPTIONS,
    incomeSourceCount
  );

  // 如果选择了 OTHER，生成备注
  const incomeSourceRemark = incomeSource.includes(IncomeSourceOptions.OTHER)
    ? fakerInstance.helpers.arrayElement([
        "亲友赠予",
        "家庭继承财产",
        "投资收益",
        "资产出售所得",
        "创业股权退出",
        "版权和专利收入",
      ])
    : undefined;

  // 确定初始资金来源
  const sameAsIncome =
    initialFundSameAsIncome !== undefined
      ? initialFundSameAsIncome
      : fakerInstance.datatype.boolean();

  // 生成初始资金来源
  const initialFundSourceSources = sameAsIncome
    ? []
    : fakerInstance.helpers.arrayElements(
        INCOME_SOURCE_OPTIONS,
        fakerInstance.number.int({ min: 1, max: 3 })
      );

  const initialFundSource = {
    sameAsIncome,
    sources: initialFundSourceSources,
  };

  // 如果初始资金来源包含 OTHER，生成备注
  const initialFundSourceRemark = initialFundSourceSources.includes(
    IncomeSourceOptions.OTHER
  )
    ? fakerInstance.helpers.arrayElement([
        "历年工作积蓄",
        "家庭资产支持",
        "投资理财收益",
        "房产出售所得",
        "企业经营利润",
        "股权投资回报",
      ])
    : undefined;

  // 确定交易目的（1-3 个）
  const transactionPurpose = fakerInstance.helpers.arrayElements(
    TRANSACTION_PURPOSE_OPTIONS,
    fakerInstance.number.int({ min: 1, max: 3 })
  );

  // 生成净资产和占比
  const netWorth = generateNetWorth(fakerInstance);
  const otherPlatformNetWorthRatio = generateAssetRatio(fakerInstance);

  // 确定是否为美国公民
  const isUSPerson = specifiedIsUSPerson;

  // 如果不是美国公民，生成税务管辖区域（必填）
  const tins =
    isUSPerson === "NO" ? generateTaxJurisdictions(fakerInstance) : undefined;

  return {
    employmentStatus,
    industry,
    annualIncome,
    incomeSourceCountry,
    incomeSource,
    incomeSourceRemark,
    initialFundSource,
    initialFundSourceRemark,
    transactionPurpose,
    netWorth,
    otherPlatformNetWorthRatio,
    isUSPerson,
    tins,
  };
}

/**
 * 批量生成财务信息表单模拟数据
 *
 * @param count - 生成数据的数量
 * @param options - 生成配置选项
 * @returns 模拟数据数组
 *
 * @example
 * ```typescript
 * // 生成 10 条测试数据
 * const mockDataList = generateFinancialInfoMockDataBatch(10);
 *
 * // 生成 5 条在职人员数据
 * const employedDataList = generateFinancialInfoMockDataBatch(5, {
 *   employmentStatus: 'EMPLOYED'
 * });
 * ```
 */
export function generateFinancialInfoMockDataBatch(
  count: number,
  options: FinancialMockDataGeneratorOptions = {}
): FinancialInfoFormData[] {
  return Array.from({ length: count }, (_, index) => {
    const itemOptions =
      options.seed !== undefined
        ? { ...options, seed: options.seed + index }
        : options;

    return generateFinancialInfoMockData(itemOptions);
  });
}

/**
 * 生成特定场景的模拟数据
 */
export const financialMockDataPresets = {
  /**
   * 全职在职 - 科技行业
   */
  fullTimeEmployedTech: (): FinancialInfoFormData =>
    generateFinancialInfoMockData({
      employmentStatus: EmploymentStatusOptions.FULL_TIME_EMPLOYED,
      industry: IndustryOptions.Information_and_Communication_Workers,
    }),

  /**
   * 自雇人士
   */
  selfEmployed: (): FinancialInfoFormData =>
    generateFinancialInfoMockData({
      employmentStatus: EmploymentStatusOptions.SELF_EMPLOYED,
      industry: IndustryOptions.Self_employed,
    }),

  /**
   * 高净值人士
   */
  highNetWorth: (): FinancialInfoFormData => {
    const data = generateFinancialInfoMockData({});
    // 覆盖净资产为更高的值
    return {
      ...data,
      annualIncome: IncomeOptions.more_than_2000000,
      netWorth: String(faker.number.int({ min: 5000000, max: 20000000 })),
    };
  },

  /**
   * 学生
   */
  student: (): FinancialInfoFormData =>
    generateFinancialInfoMockData({
      employmentStatus: EmploymentStatusOptions.STUDENT,
      industry: IndustryOptions.Education_Service_Workers,
    }),

  /**
   * 退休人士
   */
  retired: (): FinancialInfoFormData =>
    generateFinancialInfoMockData({
      employmentStatus: EmploymentStatusOptions.RETIRED,
      industry: IndustryOptions.Retired,
    }),
};

/* ============================================================================
 * 导出
 * ========================================================================== */

export default {
  generateFinancialInfoMockData,
  generateFinancialInfoMockDataBatch,
  financialMockDataPresets,
};
